import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';

import type { Attribute } from '../../../types';

export type FieldCode = Attribute['id'];

/**
 * フィールド依存関係解決フック（/v2/field-dependency/resolve使用）
 *
 * @param sourceFieldCode 依存元フィールドのコード
 * @param targetFieldCode 依存先フィールドのコード
 * @param sourceFieldValue 依存元フィールドの値
 * @param options オプション設定
 */
export function useFieldDependencyResolve(
  sourceFieldCode: FieldCode,
  targetFieldCode: FieldCode,
  sourceFieldValue: string | null,
  {
    enabled = true,
  }: {
    enabled?: boolean;
  } = {},
) {
  const { isLoading, error, data } = useQuery({
    queryKey: [
      'field-dependency-resolve',
      { sourceFieldCode, targetFieldCode, sourceFieldValue },
    ] as const,
    queryFn: async ({ queryKey: [, params] }) => {
      if (!sourceFieldValue) {
        throw new Error('Source field value is required');
      }

      const res = await apiClient.POST('/v2/field-dependency/resolve', {
        body: {
          sourceFieldCode: params.sourceFieldCode,
          targetFieldCode: params.targetFieldCode,
          sourceValue: params.sourceFieldValue as string,
        },
      });

      return getDataOrThrowApiError(res);
    },
    enabled:
      enabled && !!sourceFieldValue && !!sourceFieldCode && !!targetFieldCode,
    staleTime: 5 * 60 * 1000, // 5分間のキャッシュ
  });

  return {
    // データ関連
    options: data?.options || [],
    fieldConfig: data?.fieldConfig,

    // 状態関連
    isLoading,
    error,
    success: data?.success,

    // 派生状態
    hasError: !!error || data?.success === false,
    isEmpty: !isLoading && !error && data?.options?.length === 0,
    isReady: !isLoading && !error && !!data,
  };
}
